import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RotateCcw, Trash2, Upload } from 'lucide-react';
import { Badge, Button, DropZone } from '@bliks-io/ui';
import {
  PARTNER_NEWS_IMAGE_CONTENT_TYPES,
  PARTNER_NEWS_IMAGE_FORMATS,
  type PartnerNewsImageSlot
} from '@bliks-io/contracts';
import { IMAGE_REJECTION_KEYS, checkNewsImage } from './newsForm';

const ACCEPT = PARTNER_NEWS_IMAGE_FORMATS.map(
  (format) => PARTNER_NEWS_IMAGE_CONTENT_TYPES[format]
).join(',');

const SLOT_KEYS: Record<PartnerNewsImageSlot, { label: string; hint: string }> = {
  logo: { label: 'partnerContent.news.images.logo', hint: 'partnerContent.news.images.logoHint' },
  image: { label: 'partnerContent.news.images.image', hint: 'partnerContent.news.images.imageHint' }
};

interface NewsImageSlotProps {
  slot: PartnerNewsImageSlot;
  /** What the card would show: a picked file's preview, else the stored picture. */
  previewUrl: string | undefined;
  isPending: boolean;
  hasStored: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  onUndo: () => void;
}

/** One picture of the card: shown with replace and remove, or a drop target. */
export function NewsImageSlot({
  slot,
  previewUrl,
  isPending,
  hasStored,
  onPick,
  onRemove,
  onUndo
}: NewsImageSlotProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const label = t(SLOT_KEYS[slot].label);

  const accept = (file: File | undefined) => {
    if (!file) return;
    const check = checkNewsImage(file);
    if ('rejected' in check) {
      toast.error(t(IMAGE_REJECTION_KEYS[check.rejected]));
      return;
    }
    onPick(file);
  };

  if (!previewUrl) {
    return (
      <div className="flex flex-col gap-2">
        <DropZone
          id={`news-image-${slot}`}
          accept={ACCEPT}
          // The size limit is checked with a message; DropZone would drop it silently.
          maxSize={Number.POSITIVE_INFINITY}
          onChange={(files) => accept(files[0])}
          title={label}
          description={t(SLOT_KEYS[slot].hint)}
        />
        {hasStored && (
          <Button type="button" variant="ghost" size="small" onClick={onUndo}>
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            {t('partnerContent.actions.undo')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-md border px-3 py-2">
      <img
        src={previewUrl}
        alt={label}
        className={
          slot === 'logo' ? 'h-8 w-24 object-contain' : 'h-16 w-16 rounded-md object-cover'
        }
      />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-sm font-medium">{label}</span>
        {isPending && (
          <Badge colorScheme="warning" size="sm">
            {t('partnerContent.materials.files.uploadOnSave')}
          </Badge>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        aria-label={t('partnerContent.news.images.choose', { label })}
        onChange={(event) => {
          accept(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {isPending ? (
        <Button type="button" variant="ghost" size="small" onClick={onUndo}>
          <RotateCcw className="mr-2 h-3.5 w-3.5" />
          {t('partnerContent.actions.undo')}
        </Button>
      ) : (
        <>
          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('partnerContent.materials.files.replace')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('partnerContent.news.images.remove', { label })}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
